import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Steps,
  Button,
  message,
  Upload,
  Row,
  Col,
  Card,
  Space,
  Tabs,
} from "antd";
import AddOpponentModal from "../opponents/AddOpponentModal";
import AddPaymentModal from "../payments/addPayment";
import axiosInstance from "../../utils/axios.config";
import logger from "../../utils/logger";
import {
  UserOutlined,
  FileTextOutlined,
  DollarOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
  BankOutlined,
  HomeOutlined,
  PhoneOutlined,
  MailOutlined,
  IdcardOutlined,
  TeamOutlined,
  FolderOutlined,
  FileProtectOutlined,
  WalletOutlined,
  FileSearchOutlined,
  LoadingOutlined,
  FileOutlined,
} from "@ant-design/icons";
import {
  clientsService,
  casesService,
  paymentsService,
  documentsService,
} from "../../services/api.service";
import dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/en_US";
import AddClientModal from "../clients/AddClientModal";
import EditClientModal from "../clients/EditClientModal";
import AddCaseModal from "../case/AddCaseModal";
import EditCaseModal from "../case/EditCaseModal";

const { Dragger } = Upload;
const { Option } = Select;
const { TextArea } = Input;
const { Step } = Steps;
const { TabPane } = Tabs;

const RegistrationWizard = ({ visible: open, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // بيانات العميل
  const [clientData, setClientData] = useState({
    id: null,
    name: "",
    phone: "",
    email: "",
    address: "",
    national_id: "",
    type: "", // individual or company
    power_of_attorney_date: null,
    power_of_attorney_number: "",
    power_of_attorney_record: "",
  });

  // بيانات الخصم
  const [opponentData, setOpponentData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    opponent_type: "",
    lawyer_name: "",
    lawyer_phone: "",
    lawyer_office: "",
  });

  // بيانات الخصوم
  const [opponents, setOpponents] = useState([]);
  const [showOpponentModal, setShowOpponentModal] = useState(false);
  const [editingOpponent, setEditingOpponent] = useState(null);

  // حالة مودال العميل
  const [showClientModal, setShowClientModal] = useState(false);

  // حالة مودال القضية
  const [showCaseModal, setShowCaseModal] = useState(false);
  const [showEditCaseModal, setShowEditCaseModal] = useState(false);

  // حالة مودال الدفعة
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // فتح مودال القضية مع تمرير بيانات الخصم
  const handleShowCaseModal = () => {
    // التأكد من تحديث caseData قبل فتح المودال
    setTimeout(() => {
      setShowCaseModal(true);
    }, 100);
  };

  // فتح مودال الدفعة مع تمرير بيانات العميل والقضية
  const handleShowPaymentModal = () => {
    // التأكد من تحديث البيانات قبل فتح المودال
    setTimeout(() => {
      setShowPaymentModal(true);
    }, 100);
  };

  // معالجة نجاح إضافة الدفعة
  const handlePaymentSuccess = (newPaymentData) => {
    logger.debug("Payment added successfully:", newPaymentData);
    // استخراج بيانات الدفعة من الاستجابة
    const payment = newPaymentData.payment || newPaymentData;
    setPaymentData({
      amount: payment.amount,
      payment_date: payment.payment_date,
      payment_method: payment.payment_type,
      payment_type: payment.payment_type,
      notes: payment.description,
      case_id: payment.case_id,
      status: payment.status || "pending",
    });

    // تحديث معرف القضية إذا كان موجوداً
    if (payment.case_id) {
      setCreatedCaseId(payment.case_id);
    }

    setShowPaymentModal(false);

    // الانتقال إلى المرحلة التالية
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  // دوال معالجة الخصوم
  const handleAddOpponent = () => {
    setEditingOpponent(null);
    // عند فتح نموذج إضافة خصم من مرحلة الخصوم، قم بتمرير autoNextStep=true
    // للانتقال التلقائي للمرحلة التالية بعد إضافة الخصم
    setShowOpponentModal(true);
  };

  const handleEditOpponent = (opponent) => {
    // تمرير جميع بيانات الخصم عند التعديل
    setEditingOpponent({
      id: opponent.id,
      name: opponent.name,
      opponent_type: opponent.opponent_type || opponent.type,
      phone: opponent.phone,
      email: opponent.email,
      address: opponent.address,
      lawyer_name: opponent.lawyer_name,
      lawyer_phone: opponent.lawyer_phone,
      lawyer_office: opponent.lawyer_office,
      notes: opponent.notes,
      status: opponent.status || "active",
    });
    setShowOpponentModal(true);
  };

  const handleDeleteOpponent = (opponentId) => {
    setOpponents(opponents.filter((o) => o.id !== opponentId));
  };

  const handleOpponentSubmit = (values, autoNextStep = false) => {
    if (editingOpponent) {
      // تعديل خصم موجود
      setOpponents(
        opponents.map((o) =>
          o.id === editingOpponent.id
            ? { ...values, id: editingOpponent.id }
            : o
        )
      );
    } else {
      // إضافة خصم جديد
      const newOpponent = { ...values, id: Date.now() };
      setOpponents([...opponents, newOpponent]);

      // تمرير بيانات الخصم إلى المرحلة التالية (القضية)
      setCaseData((prev) => ({
        ...prev,
        opponent_name: newOpponent.name,
        opponent_id: newOpponent.id,
        opponent_phone: newOpponent.phone,
        opponent_type: newOpponent.opponent_type || newOpponent.type,
        opponent_address: newOpponent.address,
        opponent_lawyer: newOpponent.lawyer_name,
        opponent_lawyer_phone: newOpponent.lawyer_phone,
        opponent_lawyer_office: newOpponent.lawyer_office,
      }));

      // إذا كان autoNextStep صحيحاً وليس في وضع التعديل، انتقل للمرحلة التالية
      if (autoNextStep && !editingOpponent) {
        nextStep();
      }
    }
    setShowOpponentModal(false);
  };

  // حالة مودال تعديل العميل
  const [showEditClientModal, setShowEditClientModal] = useState(false);

  // مستمسكات العميل
  const [clientDocuments, setClientDocuments] = useState([]);
  const [clientDocumentFiles, setClientDocumentFiles] = useState([]);

  // حالة نافذة تفاصيل المستند
  const [showFileDetailsModal, setShowFileDetailsModal] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [documentDetails, setDocumentDetails] = useState({
    title: "",
    document_type: "",
    notes: "",
  });
  const [uploading, setUploading] = useState(false);

  // بيانات القضية
  const [caseData, setCaseData] = useState({
    id: null,
    case_number: "",
    title: "",
    case_type: "",
    court: "",
    court_name: "",
    court_case_number: "",
    status: "نشط",
    filing_date: new Date().toISOString().split("T")[0],
    next_session_date: null,
    description: "",
    notes: "",
    agreed_fees: null,
    client_id: null,
    opponent_name: "",
    opponent_id: null,
    opponent_phone: "",
    opponent_type: "",
    opponent_address: "",
    opponent_lawyer: "",
    opponent_lawyer_phone: "",
    opponent_lawyer_office: "",
  });

  // بيانات الدفعة
  const [paymentData, setPaymentData] = useState({
    amount: "",
    payment_date: new Date().toISOString().split("T")[0],
    payment_method: "cash",
    payment_type: "initial",
    notes: "",
    case_id: null,
    status: "pending",
  });

  // مستندات القضية
  const [caseDocuments, setCaseDocuments] = useState([]);
  const [caseDocumentFiles, setCaseDocumentFiles] = useState([]);

  // حالة النموذج
  const [errors, setErrors] = useState({});
  const [createdClientId, setCreatedClientId] = useState(null);
  const [createdCaseId, setCreatedCaseId] = useState(null);
  const [activeTab, setActiveTab] = useState("1");

  // خطوات النموذج
  const steps = [
    { id: 1, title: "معلومات العميل", icon: IdcardOutlined },
    { id: 2, title: "مستمسكات العميل", icon: FolderOutlined },
    { id: 3, title: "معلومات الخصم", icon: TeamOutlined },
    { id: 4, title: "تفاصيل القضية", icon: FileProtectOutlined },
    { id: 5, title: "المدفوعات", icon: WalletOutlined },
    { id: 6, title: "مرفقات القضية", icon: FileSearchOutlined },
  ];

  // جلب بيانات العميل عند العودة للمرحلة الأولى
  useEffect(() => {
    if (currentStep === 1 && createdClientId && !clientData.name) {
      fetchClientData(createdClientId);
    }
  }, [currentStep, createdClientId, clientData.name]);

  // إعادة تعيين النموذج عند الإغلاق
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  const resetForm = () => {
    setCurrentStep(1);
    setClientData({
      name: "",
      phone: "",
      email: "",
      address: "",
      national_id: "",
      type: "individual",
    });
    setShowClientModal(false);
    setClientDocuments([]);
    setClientDocumentFiles([]);
    setCaseData({
      case_number: "",
      case_type: "",
      court: "",
      status: "نشط",
      filing_date: new Date().toISOString().split("T")[0],
      description: "",
      client_id: null,
    });
    setPaymentData({
      amount: "",
      payment_date: new Date().toISOString().split("T")[0],
      payment_method: "cash",
      payment_type: "initial",
      notes: "",
      case_id: null,
    });
    setCaseDocuments([]);
    setCaseDocumentFiles([]);
    setErrors({});
    setCreatedClientId(null);
    setCreatedCaseId(null);
    form.resetFields();
  };

  // التحقق من صحة بيانات العميل
  const validateClientData = () => {
    logger.debug("Validating client data:", { createdClientId, clientData });
    if (!createdClientId) {
      message.error("الرجاء إضافة بيانات العميل أولاً");
      return false;
    }

    if (!clientData.name) {
      message.error("الرجاء إدخال اسم العميل");
      return false;
    }

    if (!clientData.national_id) {
      message.error("الرجاء إدخال رقم الهوية");
      return false;
    }

    logger.debug("Client data validated successfully");
    return true;
  };

  // التحقق من صحة بيانات القضية
  const validateCaseData = () => {
    const newErrors = {};

    if (!caseData.case_number?.trim()) {
      newErrors.case_number = "رقم القضية مطلوب";
    }

    if (!clientData.name?.trim()) {
      newErrors.client_name = "اسم العميل مطلوب";
    }

    if (!caseData.case_type?.trim()) {
      newErrors.case_type = "نوع القضية مطلوب";
    }

    if (!caseData.court?.trim()) {
      newErrors.court = "المحكمة مطلوبة";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // التحقق من صحة بيانات الدفعة
  const validatePaymentData = () => {
    const newErrors = {};

    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      newErrors.amount = "مبلغ الدفعة مطلوب ويجب أن يكون أكبر من صفر";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // الانتقال إلى الخطوة التالية
  const nextStep = async () => {
    let isValid = true;

    if (currentStep === 1) {
      logger.debug("Verifying client data:", createdClientId);
      isValid = validateClientData();
      if (isValid) {
        logger.debug("Client data verified successfully:", createdClientId);
        setCaseData({ ...caseData, client_id: createdClientId });
        message.success("تم حفظ بيانات العميل بنجاح");
      }
    } else if (currentStep === 2) {
      // التحقق من وجود مستندات لم يتم رفعها بعد
      logger.debug(
        "Verifying client documents:",
        { createdClientId, clientDocuments }
      );
      const unuploadedDocuments = clientDocuments.filter(
        (doc) => !doc.uploaded
      );
      if (unuploadedDocuments.length > 0 && createdClientId) {
        setLoading(true);
        try {
          // رفع المستندات التي لم يتم رفعها بعد
          const updatedDocuments = [...clientDocuments];
          for (let i = 0; i < updatedDocuments.length; i++) {
            const document = updatedDocuments[i];
            if (!document.uploaded) {
              const file = clientDocumentFiles[i];

              logger.debug("Uploading document:", document.title);
              await documentsService.uploadClientDocument(
                createdClientId,
                file,
                {
                  title: document.title,
                  document_type: document.document_type,
                  notes: document.notes,
                }
              );

              // تحديث حالة المستند إلى مرفوع
              updatedDocuments[i].uploaded = true;
            }
          }
          setClientDocuments(updatedDocuments);
          message.success("تم حفظ مستمسكات العميل بنجاح");
        } catch (error) {
          logger.error("Error uploading client documents:", error);
          message.error("فشل في حفظ مستمسكات العميل");
          isValid = false;
        } finally {
          setLoading(false);
        }
      }
    } else if (currentStep === 3) {
      // مرحلة الخصوم
      logger.debug("Opponents data:", opponentData);
      // تمرير بيانات الخصم إلى المرحلة التالية
      setCaseData((prev) => ({
        ...prev,
        opponent_id: opponentData.id,
        opponent_name: opponentData.name,
        opponent_type: opponentData.opponent_type,
        opponent_phone: opponentData.phone,
        opponent_address: opponentData.address,
        opponent_lawyer: opponentData.lawyer_name,
        opponent_lawyer_phone: opponentData.lawyer_phone,
        opponent_lawyer_office: opponentData.lawyer_office,
      }));
    } else if (currentStep === 4) {
      logger.debug("Verifying case data:", caseData);
      // تحديث opponentData من caseData
      setOpponentData({
        id: caseData.opponent_id,
        name: caseData.opponent_name,
        phone: caseData.opponent_phone,
        email: caseData.opponent_email,
        address: caseData.opponent_address,
        opponent_type: caseData.opponent_type,
        lawyer_name: caseData.opponent_lawyer,
        lawyer_phone: caseData.opponent_lawyer_phone,
        lawyer_office: caseData.opponent_lawyer_office,
      });
      isValid = validateCaseData();
      if (isValid) {
        setLoading(true);
        try {
          // تحديث أو إنشاء القضية
          const caseDataToSend = {
            ...caseData,
            court_name: caseData.court || caseData.court_name,
            client_name: clientData.name,
          };

          // تحديث معرف القضية إذا كان موجوداً
          if (caseData.id) {
            setCreatedCaseId(caseData.id);
          }
        } catch (error) {
          logger.error("Error saving case:", error);
          message.error("فشل في حفظ القضية");
          isValid = false;
        } finally {
          setLoading(false);
        }
      }
    } else if (currentStep === 5) {
      logger.debug("Verifying payment data:", paymentData);

      // التحقق مما إذا كان المستخدم يريد إضافة دفعة
      if (paymentData.amount) {
        isValid = validatePaymentData();
        if (isValid) {
          setLoading(true);
          try {
            // تسجيل الدفعة
            logger.debug("Registering payment:", paymentData);
            const response = await paymentsService.createPayment(paymentData);

            // استخراج بيانات الدفعة من الاستجابة
            const paymentResponse = response.data || response;
            logger.debug("Payment registered successfully:", paymentResponse);

            message.success("تم تسجيل الدفعة بنجاح");
          } catch (error) {
            logger.error("Error creating payment:", error);
            message.error("فشل في تسجيل الدفعة");
            isValid = false;
          } finally {
            setLoading(false);
          }
        }
      } else {
        // إذا لم يتم إدخال بيانات دفعة، يمكن الانتقال مباشرة
        logger.debug("Payment step skipped");
        message.info("تم تخطي مرحلة إضافة الدفعة");
      }
    } else if (currentStep === 6) {
      // حفظ مستندات القضية
      logger.debug("Verifying case documents:", { createdCaseId, caseDocuments });
      if (caseDocumentFiles.length > 0 && createdCaseId) {
        setLoading(true);
        try {
          // رفع مستندات القضية واحدًا تلو الآخر
          for (let i = 0; i < caseDocuments.length; i++) {
            const document = caseDocuments[i];
            const file = caseDocumentFiles[i];

            logger.debug("Uploading case document:", document.title);
            const response = await documentsService.uploadDocument(
              createdCaseId,
              file
            );

            // استخراج بيانات المستند من الاستجابة
            const documentResponse = response.data || response;
            logger.debug("Case document uploaded successfully:", documentResponse);
          }
          message.success("تم حفظ مستندات القضية بنجاح");
        } catch (error) {
          logger.error("Error uploading case documents:", error);
          message.error("فشل في حفظ مستندات القضية");
          isValid = false;
        } finally {
          setLoading(false);
        }
      }
    }

    if (isValid && currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  // الانتقال إلى الخطوة السابقة
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // رفع مستمسكات العميل
  const handleClientDocumentUpload = (file) => {
    const isValidSize = file.size / 1024 / 1024 < 10; // حجم الملف أقل من 10 ميجابايت

    if (!isValidSize) {
      message.error("حجم الملف يجب أن يكون أقل من 10 ميجابايت");
      return false;
    }

    logger.debug("Verifying client existence before upload:", createdClientId);

    // عرض نافذة إدخال تفاصيل المستند مع تمرير اسم الملف كعنوان افتراضي
    setFileToUpload(file);
    setDocumentDetails({
      title: file.name,
      document_type: "",
      notes: "",
    });
    setShowFileDetailsModal(true);

    return false; // منع الرفع التلقائي
  };

  // حذف مستمسك عميل
  const removeClientDocument = (id) => {
    setClientDocuments(clientDocuments.filter((doc) => doc.id !== id));
    setClientDocumentFiles(
      clientDocumentFiles.filter(
        (_, index) =>
          clientDocuments.findIndex((doc) => doc.id === id) !== index
      )
    );
  };

  // تأكيد رفع المستند مع تفاصيله
  const handleConfirmUpload = async () => {
    if (!fileToUpload) return;

    // التحقق من إدخال العنوان ونوع المستند
    if (!documentDetails.title || !documentDetails.document_type) {
      message.error("يرجى إدخال عنوان المستند ونوع المستند");
      return;
    }

    setUploading(true);

    try {
      // إضافة المستند مع تفاصيله
      const newDocument = {
        id: Date.now(),
        name: fileToUpload.name,
        title: documentDetails.title,
        type: fileToUpload.type,
        document_type: documentDetails.document_type,
        notes: documentDetails.notes,
        size: fileToUpload.size,
        uploaded: false, // إضافة خاصية لتحديد ما إذا تم رفع المستند
      };

      // تحديد ما إذا كان المستند للعميل أو للقضية بناءً على المرحلة الحالية
      if (currentStep === 2) {
        // إذا كان العميل قد تم إنشاؤه بالفعل، قم برفع المستند مباشرةً
        logger.debug("Verifying client existence:", createdClientId);
        if (createdClientId) {
          try {
            logger.debug("Uploading document for client:", createdClientId);
            await documentsService.uploadClientDocument(
              createdClientId,
              fileToUpload,
              {
                title: documentDetails.title,
                document_type: documentDetails.document_type,
                notes: documentDetails.notes,
              }
            );

            // تحديث حالة المستند إلى مرفوع
            newDocument.uploaded = true;

            message.success("تم رفع المستند بنجاح");
          } catch (error) {
            logger.error("Error uploading document:", error);
            message.error("فشل في رفع المستند");
          }
        } else {
          logger.info("Client not created yet, document will be uploaded later");
          message.success("تم إضافة المستند، سيتم رفعه بعد إنشاء العميل");
        }

        setClientDocuments([...clientDocuments, newDocument]);
        setClientDocumentFiles([...clientDocumentFiles, fileToUpload]);
      } else if (currentStep === 6) {
        // إذا كانت القضية قد تم إنشاؤها بالفعل، قم برفع المستند مباشرةً
        logger.debug("Verifying case existence:", createdCaseId);
        if (createdCaseId) {
          try {
            logger.debug("Uploading document for case:", createdCaseId);
            await documentsService.uploadDocument(
              createdCaseId,
              fileToUpload,
              documentDetails
            );

            // تحديث حالة المستند إلى مرفوع
            newDocument.uploaded = true;

            message.success("تم رفع المستند بنجاح");
          } catch (error) {
            logger.error("Error uploading document:", error);
            message.error("فشل في رفع المستند");
          }
        } else {
          logger.info("Case not created yet, document will be uploaded later");
          message.success("تم إضافة المستند، سيتم رفعه بعد إنشاء القضية");
        }

        setCaseDocuments([...caseDocuments, newDocument]);
        setCaseDocumentFiles([...caseDocumentFiles, fileToUpload]);
      }

      // إغلاق النافذة وإعادة تعيين الحقول
      setShowFileDetailsModal(false);
      setFileToUpload(null);
      setDocumentDetails({ title: "", document_type: "", notes: "" });
    } catch (error) {
      logger.error("Error adding document:", error);
      message.error("فشل في إضافة المستند");
    } finally {
      setUploading(false);
    }
  };

  // رفع مستندات القضية
  const handleCaseDocumentUpload = (file) => {
    const isValidSize = file.size / 1024 / 1024 < 10; // حجم الملف أقل من 10 ميجابايت

    if (!isValidSize) {
      message.error("حجم الملف يجب أن يكون أقل من 10 ميجابايت");
      return false;
    }

    logger.debug("Verifying case existence before upload:", createdCaseId);

    // عرض نافذة إدخال تفاصيل المستند مع تمرير اسم الملف كعنوان افتراضي
    setFileToUpload(file);
    setDocumentDetails({
      title: file.name,
      document_type: "",
      notes: "",
    });
    setShowFileDetailsModal(true);

    return false; // منع الرفع التلقائي
  };

  // حذف مستند قضية
  const removeCaseDocument = (id) => {
    setCaseDocuments(caseDocuments.filter((doc) => doc.id !== id));
    setCaseDocumentFiles(
      caseDocumentFiles.filter(
        (_, index) => caseDocuments.findIndex((doc) => doc.id === id) !== index
      )
    );
  };

  // جلب بيانات العميل
  const fetchClientData = async (clientId) => {
    try {
      logger.debug("Fetching client data:", clientId);
      const response = await axiosInstance.get(`/clients/${clientId}`);
      const client = response.data;
      logger.debug("Client data received:", client);

      const newClientData = {
        name: client.name,
        phone: client.phone,
        email: client.email,
        address: client.address,
        national_id: client.national_id,
        type: client.client_type,
        power_of_attorney_date: client.power_of_attorney_date,
        power_of_attorney_number: client.power_of_attorney_number,
        power_of_attorney_record: client.power_of_attorney_record,
        notary_name: client.notary_name,
      };

      logger.debug("Updating client data:", newClientData);
      setClientData(newClientData);
    } catch (error) {
      logger.error("Error fetching client data:", error);
      message.error("حدث خطأ في جلب بيانات العميل");
    }
  };

  // معالجة نجاح إضافة/تعديل القضية
  const handleCaseSuccess = (response) => {
    logger.debug("Case saved successfully:", response);
    const responseData = response?.data || response || {};

    // الحفاظ على البيانات الحالية مع تحديث البيانات الجديدة فقط
    setCaseData((prevData) => ({
      ...prevData,
      id: responseData.id || prevData.id,
      case_number: responseData.case_number || prevData.case_number,
      title: responseData.title || prevData.title || prevData.case_number,
      case_type: responseData.case_type || prevData.case_type,
      court: responseData.court || responseData.court_name || prevData.court,
      court_name:
        responseData.court_name || responseData.court || prevData.court_name,
      court_case_number:
        responseData.court_case_number || prevData.court_case_number,
      status: responseData.status || prevData.status,
      filing_date: responseData.filing_date || prevData.filing_date,
      next_session_date:
        responseData.next_session_date || prevData.next_session_date,
      description: responseData.description || prevData.description,
      notes: responseData.notes || prevData.notes,
      agreed_fees: responseData.agreed_fees || prevData.agreed_fees,
      client_id: responseData.client_id || prevData.client_id,
      opponent_name: responseData.opponent_name || prevData.opponent_name,
      opponent_id: responseData.opponent_id || prevData.opponent_id,
      opponent_phone: responseData.opponent_phone || prevData.opponent_phone,
      opponent_type: responseData.opponent_type || prevData.opponent_type,
      opponent_address:
        responseData.opponent_address || prevData.opponent_address,
      opponent_lawyer: responseData.opponent_lawyer || prevData.opponent_lawyer,
      opponent_lawyer_phone:
        responseData.opponent_lawyer_phone || prevData.opponent_lawyer_phone,
      opponent_lawyer_office:
        responseData.opponent_lawyer_office || prevData.opponent_lawyer_office,
    }));

    // تحديث معرف القضية المنشأة
    if (responseData.id) {
      setCreatedCaseId(responseData.id);
    }

    setShowCaseModal(false);
    setShowEditCaseModal(false);
    message.success("تم حفظ القضية بنجاح");

    // الانتقال التلقائي إلى المرحلة الخامسة فقط عند إضافة قضية جديدة
    if (!caseData.id) {
      setCurrentStep(5);
    }
  };

  // معالجة نجاح إضافة الخصم
  const handleOpponentSuccess = (opponent, autoNextStep = false) => {
    logger.debug("handleOpponentSuccess called with:", opponent, autoNextStep);
    const newOpponents = [...opponents, opponent];
    setOpponents(newOpponents);
    setShowOpponentModal(false);
    message.success("تم إضافة الخصم بنجاح");

    // تحديث بيانات الخصم مثل clientData
    setOpponentData({
      id: opponent.id,
      name: opponent.name,
      phone: opponent.phone,
      email: opponent.email,
      address: opponent.address,
      opponent_type: opponent.opponent_type || opponent.type,
      lawyer_name: opponent.lawyer_name,
      lawyer_phone: opponent.lawyer_phone,
      lawyer_office: opponent.lawyer_office,
    });

    // التأكد من تحديث opponents
    setTimeout(() => {
      logger.debug("Updated opponents:", newOpponents);
    }, 500);

    // تمرير بيانات الخصم إلى المرحلة التالية (القضية)
    logger.debug("Updating case data with opponent:", opponent);
    setCaseData((prev) => {
      const updatedData = {
        ...prev,
        opponent_name: opponent.name,
        opponent_id: opponent.id,
        opponent_phone: opponent.phone,
        opponent_type: opponent.opponent_type || opponent.type,
        opponent_address: opponent.address,
        opponent_lawyer: opponent.lawyer_name,
        opponent_lawyer_phone: opponent.lawyer_phone,
        opponent_lawyer_office: opponent.lawyer_office,
      };
      logger.debug("Updated case data:", updatedData);
      return updatedData;
    });

    // التأكد من تحديث caseData قبل الانتقال للمرحلة التالية
    setTimeout(() => {
      logger.debug("Final case data:", caseData);
    }, 500);

    // إذا كان autoNextStep صحيحاً، انتقل للمرحلة التالية
    if (autoNextStep) {
      logger.debug("Moving to next step...");
      setTimeout(() => {
        nextStep();
      }, 100);
    }
  };

  // معالجة نجاح إضافة العميل
  const handleClientSuccess = (response) => {
    logger.debug("Client created successfully:", response);

    // استخراج بيانات العميل من الاستجابة
    const clientData = response?.data || response || {};

    setCreatedClientId(clientData.id);
    setClientData({
      id: clientData.id,
      name: clientData.name,
      phone: clientData.phone,
      email: clientData.email,
      address: clientData.address,
      national_id: clientData.national_id,
      type: clientData.client_type,
      power_of_attorney_date: clientData.power_of_attorney_date,
      power_of_attorney_number: clientData.power_of_attorney_number,
      power_of_attorney_record: clientData.power_of_attorney_record,
      notary_name: clientData.notary_name,
    });
    setShowClientModal(false);
    message.success("تم إضافة العميل بنجاح");
    // الانتقال التلقائي إلى المرحلة الثانية بعد إضافة العميل بنجاح
    setCaseData((prev) => ({ ...prev, client_id: clientData.id }));
    setCurrentStep(2);
  };

  // إكمال التسجيل
  const completeRegistration = async () => {
    setLoading(true);
    try {
      const registrationData = {
        client: clientData,
        case: caseData,
        payment: paymentData,
        clientDocuments: clientDocuments,
        caseDocuments: caseDocuments,
      };



      logger.debug("Completing registration:", registrationData);
      logger.debug("Client ID:", createdClientId);
      logger.debug("Case ID:", createdCaseId);

      // تم حفظ جميع البيانات بالفعل في كل مرحلة
      message.success("تم إكمال تسجيل القيد بنجاح");
      onSuccess();
      onClose();
    } catch (error) {
      logger.error("Error completing registration:", error);
      message.error("فشل في إكمال التسجيل");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <div
          className="text-xl font-bold text-right text-gray-800 mb-4"
          dir="rtl"
        >
          تسجيل قيد جديد
        </div>
      }
      open={open}
      closable={true}
      onCancel={onClose}
      footer={null}
      confirmLoading={loading}
      width={1000}
      dir="rtl"
      className="custom-modal"
      styles={{
        body: {
          maxHeight: "80vh",
          overflow: "auto",
          padding: "20px",
        },
      }}
    >
      <Steps
        current={currentStep - 1}
        direction="horizontal"
        size="default"
        className="mb-6 custom-steps"
        dir="rtl"
        items={steps.map((step, index) => {
          const isCompleted = index < currentStep - 1;
          const isCurrent = index === currentStep - 1;

          return {
            title: step.title,
            status: isCompleted ? "finish" : isCurrent ? "process" : "wait",
            icon: isCompleted ? (
              <CheckCircleOutlined style={{ color: "#52c41a" }} />
            ) : isCurrent ? (
              <LoadingOutlined spin />
            ) : (
              <step.icon />
            ),
          };
        })}
      />

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          type: "individual",
          status: "نشط",
          payment_method: "cash",
          payment_type: "initial",
          filing_date: dayjs(),
          payment_date: dayjs(),
        }}
        className="px-4"
        requiredMark={false}
        dir="rtl"
      >
        {currentStep === 1 && (
          <div className="text-center py-8">
            <div className="mb-8">
              <UserOutlined className="text-6xl text-blue-500 mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                بيانات العميل
              </h3>
              <p className="text-gray-600">
                قم بإضافة بيانات العميل للبدء في تسجيل القيد الجديد
              </p>
            </div>

            {createdClientId ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 max-w-md mx-auto">
                <CheckCircleOutlined className="text-4xl text-green-500 mb-3" />
                <h4 className="text-lg font-semibold text-green-800 mb-2">
                  تم إضافة العميل بنجاح
                </h4>
                <p className="text-green-700">اسم العميل: {clientData.name}</p>
                <p className="text-green-700">
                  رقم الهوية: {clientData.national_id}
                </p>
                <Button
                  type="primary"
                  className="mt-4"
                  onClick={() => setShowEditClientModal(true)}
                >
                  تعديل بيانات العميل
                </Button>
              </div>
            ) : (
              <Button
                type="primary"
                size="large"
                icon={<UserOutlined />}
                onClick={() => setShowClientModal(true)}
              >
                إضافة بيانات العميل
              </Button>
            )}
          </div>
        )}

        {currentStep === 2 && (
          <div>
            <Dragger
              name="documents"
              multiple
              beforeUpload={handleClientDocumentUpload}
              showUploadList={false}
              className="mb-4"
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined className="text-4xl text-blue-500" />
              </p>
              <p className="ant-upload-text text-gray-700">
                انقر أو اسحب الملفات إلى هذه المنطقة للرفع
              </p>
              <p className="ant-upload-hint text-gray-500">
                دعم رفع ملفات متعددة. الحجم الأقصى للملف: 10 ميجابايت
              </p>
            </Dragger>

            {clientDocuments.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-gray-700">الملفات المرفوعة:</h4>
                {clientDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <FileTextOutlined className="h-5 w-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-gray-800">{doc.name}</p>
                        <p className="text-xs text-gray-500">
                          {(doc.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                    <Button
                      type="text"
                      size="small"
                      onClick={() => removeClientDocument(doc.id)}
                      className="text-red-500 hover:text-red-700"
                      icon={<CheckCircleOutlined />}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {currentStep === 3 && (
          <div className="text-center py-8">
            <div className="mb-8">
              <TeamOutlined className="text-6xl text-blue-500 mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                إضافة الخصوم
              </h3>
              <p className="text-gray-600">قم بإضافة الخصوم في القضية</p>
            </div>

            <div className="space-y-4">
              {opponents.map((opponent, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div className="text-right" style={{ width: "70%" }}>
                      <p className="font-medium">{opponent.name}</p>
                      <p className="text-sm text-gray-600">
                        النوع: {opponent.opponent_type || opponent.type}
                      </p>
                      {opponent.phone && (
                        <p className="text-sm text-gray-600">
                          الهاتف: {opponent.phone}
                        </p>
                      )}
                      {opponent.email && (
                        <p className="text-sm text-gray-600">
                          البريد الإلكتروني: {opponent.email}
                        </p>
                      )}
                      {opponent.address && (
                        <p className="text-sm text-gray-600">
                          العنوان: {opponent.address}
                        </p>
                      )}
                      {opponent.lawyer_name && (
                        <p className="text-sm text-gray-600">
                          المحامي: {opponent.lawyer_name}
                        </p>
                      )}
                    </div>
                    <Space>
                      <Button
                        type="text"
                        onClick={() => handleEditOpponent(opponent)}
                      >
                        تعديل
                      </Button>
                      <Button
                        type="text"
                        danger
                        onClick={() => {
                          const newOpponents = opponents.filter(
                            (_, i) => i !== index
                          );
                          setOpponents(newOpponents);
                        }}
                      >
                        حذف
                      </Button>
                    </Space>
                  </div>
                </div>
              ))}

              <Button
                type="primary"
                size="large"
                icon={<TeamOutlined />}
                onClick={() => {
                  setEditingOpponent(null);
                  setShowOpponentModal(true);
                }}
              >
                إضافة خصم جديد
              </Button>
            </div>
          </div>
        )}

        {currentStep === 4 && (
          <div>
            {caseData.id ? (
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="font-medium text-gray-700 mb-2">
                  بيانات القضية:
                </h4>
                <p>رقم القضية: {caseData.case_number}</p>
                <p>نوع القضية: {caseData.case_type}</p>
                <p>المحكمة: {caseData.court}</p>
                <p>الحالة: {caseData.status}</p>
                <div className="mt-4">
                  <Button
                    type="primary"
                    onClick={() => setShowEditCaseModal(true)}
                    icon={<FileProtectOutlined />}
                  >
                    تعديل القضية
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <FileProtectOutlined className="text-6xl text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  إضافة قضية جديدة
                </h3>
                <p className="text-gray-500 mb-4">
                  قم بإضافة بيانات القضية المرتبطة بالعميل
                </p>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleShowCaseModal}
                  icon={<FileProtectOutlined />}
                >
                  إضافة قضية جديدة
                </Button>
              </div>
            )}
          </div>
        )}

        {currentStep === 5 && (
          <div className="text-center py-8">
            <div className="mb-6">
              <WalletOutlined className="text-6xl text-blue-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">إضافة دفعة</h3>
              <p className="text-gray-600">
                اضغط على الزر أدناه لإضافة دفعة جديدة للقضية (اختياري)
              </p>
            </div>
            <div className="flex gap-4 justify-center">
              <Button
                type="primary"
                size="large"
                icon={<DollarOutlined />}
                onClick={handleShowPaymentModal}
                className="px-8 py-3 text-lg"
              >
                إضافة دفعة جديدة
              </Button>
            </div>
            {paymentData.amount && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-lg font-semibold mb-3 text-gray-800">
                  معلومات الدفعة
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <span className="text-gray-600">المبلغ:</span>
                    <span className="mr-2 font-semibold">
                      {paymentData.amount} دينار
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">تاريخ الدفع:</span>
                    <span className="mr-2 font-semibold">
                      {paymentData.payment_date}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">طريقة الدفع:</span>
                    <span className="mr-2 font-semibold">
                      {paymentData.payment_method === "cash"
                        ? "نقدي"
                        : paymentData.payment_method === "card"
                        ? "بطاقة ائتمان"
                        : paymentData.payment_method === "transfer"
                        ? "تحويل بنكي"
                        : paymentData.payment_method === "check"
                        ? "شيك"
                        : paymentData.payment_method}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">نوع الدفعة:</span>
                    <span className="mr-2 font-semibold">
                      {paymentData.payment_type === "initial"
                        ? "دفعة أولية"
                        : paymentData.payment_type === "installment"
                        ? "قسط"
                        : paymentData.payment_type === "final"
                        ? "دفعة نهائية"
                        : paymentData.payment_type === "other"
                        ? "أخرى"
                        : paymentData.payment_type}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">حالة الدفعة:</span>
                    <span className="mr-2 font-semibold">
                      {paymentData.status === "pending"
                        ? "معلق"
                        : paymentData.status === "paid"
                        ? "مدفوع"
                        : paymentData.status === "cancelled"
                        ? "ملغي"
                        : paymentData.status}
                    </span>
                  </div>
                  {paymentData.notes && (
                    <div className="md:col-span-2">
                      <span className="text-gray-600">ملاحظات:</span>
                      <span className="mr-2 font-semibold">
                        {paymentData.notes}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {currentStep === 6 && (
          <div>
            <Dragger
              name="documents"
              multiple
              beforeUpload={handleCaseDocumentUpload}
              showUploadList={false}
              className="mb-4"
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined className="text-4xl text-blue-500" />
              </p>
              <p className="ant-upload-text text-gray-700">
                انقر أو اسحب الملفات إلى هذه المنطقة للرفع
              </p>
              <p className="ant-upload-hint text-gray-500">
                دعم رفع ملفات متعددة. الحجم الأقصى للملف: 10 ميجابايت
              </p>
            </Dragger>

            {caseDocuments.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-gray-700">الملفات المرفوعة:</h4>
                {caseDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <FileTextOutlined className="h-5 w-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-gray-800">
                          {doc.title || doc.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(doc.size / 1024).toFixed(2)} KB
                        </p>
                        {doc.document_type && (
                          <p className="text-xs text-gray-500">
                            النوع: {doc.document_type}
                          </p>
                        )}
                        {doc.uploaded && (
                          <span className="text-xs text-green-600">
                            تم الرفع
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      type="text"
                      size="small"
                      onClick={() => removeCaseDocument(doc.id)}
                      className="text-red-500 hover:text-red-700"
                      icon={<CheckCircleOutlined />}
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">ملخص التسجيل:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• العميل: {clientData.name}</li>
                <li>• القضية: {caseData.case_number}</li>
                <li>
                  • الدفعة:{" "}
                  {paymentData.amount
                    ? `${paymentData.amount} دينار (${
                        paymentData.status === "pending"
                          ? "معلق"
                          : paymentData.status === "paid"
                          ? "مدفوع"
                          : paymentData.status === "cancelled"
                          ? "ملغي"
                          : paymentData.status
                      })`
                    : "غير محدد"}
                </li>
                <li>
                  • المستمسكات المرفوعة للعميل: {clientDocuments.length} ملف
                </li>
                <li>• المستندات المرفوعة للقضية: {caseDocuments.length} ملف</li>
              </ul>
            </div>
          </div>
        )}
      </Form>

      {/* إضافة مودال الخصم */}
      <AddOpponentModal
        isOpen={showOpponentModal}
        onClose={() => setShowOpponentModal(false)}
        onOpponentAdded={(opponent, autoNextStep) =>
          handleOpponentSuccess(opponent, autoNextStep)
        }
        autoNextStep={true}
      />

      {/* إضافة مودال العميل */}
      <AddClientModal
        visible={showClientModal}
        onClose={() => setShowClientModal(false)}
        onSuccess={handleClientSuccess}
        initialName={clientData.name}
      />

      {/* تعديل مودال العميل */}
      <EditClientModal
        visible={showEditClientModal}
        onClose={() => setShowEditClientModal(false)}
        onSuccess={handleClientSuccess}
        client={{
          id: createdClientId,
          name: clientData.name,
          phone: clientData.phone,
          email: clientData.email,
          address: clientData.address,
          national_id: clientData.national_id,
          client_type: clientData.type,
          power_of_attorney_date: clientData.power_of_attorney_date
            ? dayjs(clientData.power_of_attorney_date)
            : null,
          power_of_attorney_number: clientData.power_of_attorney_number,
          power_of_attorney_record: clientData.power_of_attorney_record,
          notary_name: clientData.notary_name,
        }}
      />

      {/* إضافة مودال القضية */}
      {logger.debug("Passing opponentData to AddCaseModal:", opponentData)}
      <AddCaseModal
        visible={showCaseModal}
        onClose={() => setShowCaseModal(false)}
        onSuccess={handleCaseSuccess}
        initialClientData={{
          id: createdClientId,
          name: clientData.name,
          phone: clientData.phone,
          email: clientData.email,
          address: clientData.address,
          national_id: clientData.national_id,
        }}
        initialOpponentData={{
          id: opponentData.id,
          name: opponentData.name,
          phone: opponentData.phone,
          type: opponentData.opponent_type,
          address: opponentData.address,
          lawyer_name: opponentData.lawyer_name,
          lawyer_phone: opponentData.lawyer_phone,
          lawyer_office: opponentData.lawyer_office,
          type: clientData.type,
          power_of_attorney_date: clientData.power_of_attorney_date,
          power_of_attorney_number: clientData.power_of_attorney_number,
          power_of_attorney_record: clientData.power_of_attorney_record,
          notary_name: clientData.notary_name,
        }}
      />

      {/* تعديل مودال القضية */}
      <EditCaseModal
        visible={showEditCaseModal}
        onCancel={() => setShowEditCaseModal(false)}
        onSubmit={handleCaseSuccess}
        initialData={{
          id: caseData.id,
          case_number: caseData.case_number,
          title: caseData.title,
          client_name: clientData.name,
          client_id: createdClientId,
          court_name: caseData.court || caseData.court_name,
          case_type: caseData.case_type,
          court: caseData.court || caseData.court_name,
          status: caseData.status,
          description: caseData.description,
          notes: caseData.notes || "",
          court_case_number: caseData.court_case_number || "",
          filing_date: caseData.filing_date,
          next_session_date: caseData.next_session_date || null,
          phone: clientData.phone || "",
          agreed_fees: caseData.agreed_fees || null,
          opponent_name: caseData.opponent_name || "",
          opponent_id: caseData.opponent_id || null,
          opponent_phone: caseData.opponent_phone || "",
          opponent_type: caseData.opponent_type || "",
          opponent_address: caseData.opponent_address || "",
          opponent_lawyer: caseData.opponent_lawyer || "",
          opponent_lawyer_phone: caseData.opponent_lawyer_phone || "",
          opponent_lawyer_office: caseData.opponent_lawyer_office || "",
        }}
      />

      {/* File Details Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <FileOutlined className="text-blue-500" />
            تفاصيل المستند
          </div>
        }
        open={showFileDetailsModal}
        onOk={handleConfirmUpload}
        onCancel={() => {
          setShowFileDetailsModal(false);
          setFileToUpload(null);
          setDocumentDetails({ title: "", document_type: "", notes: "" });
        }}
        okText={uploading ? "جاري الرفع..." : "رفع المستند"}
        cancelText="إلغاء"
        confirmLoading={uploading}
        width={600}
        className="rtl-modal"
      >
        <div className="py-4">
          {fileToUpload && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">الملف المحدد:</p>
              <p className="font-medium text-gray-800">{fileToUpload.name}</p>
              <p className="text-xs text-gray-500">
                الحجم: {(fileToUpload.size / 1024 / 1024).toFixed(2)} ميجابايت
              </p>
            </div>
          )}

          <Form layout="vertical" className="space-y-4">
            <Form.Item label="عنوان المستند" required>
              <Input
                value={documentDetails.title}
                onChange={(e) =>
                  setDocumentDetails({
                    ...documentDetails,
                    title: e.target.value,
                  })
                }
                placeholder="أدخل عنوان وصفي للمستند"
                className="text-right"
              />
            </Form.Item>

            <Form.Item label="نوع المستند" required>
              <Select
                value={documentDetails.document_type}
                onChange={(value) =>
                  setDocumentDetails({
                    ...documentDetails,
                    document_type: value,
                  })
                }
                placeholder="اختر نوع المستند"
                className="text-right"
              >
                <Select.Option value="هوية شخصية">هوية شخصية</Select.Option>
                <Select.Option value="جواز سفر">جواز سفر</Select.Option>
                <Select.Option value="شهادة ميلاد">شهادة ميلاد</Select.Option>
                <Select.Option value="عقد">عقد</Select.Option>
                <Select.Option value="فاتورة">فاتورة</Select.Option>
                <Select.Option value="ملف PDF">ملف PDF</Select.Option>
                <Select.Option value="مستند Word">مستند Word</Select.Option>
                <Select.Option value="جدول Excel">جدول Excel</Select.Option>
                <Select.Option value="صورة">صورة</Select.Option>
                <Select.Option value="ملف مضغوط">ملف مضغوط</Select.Option>
                <Select.Option value="مستند قانوني">مستند قانوني</Select.Option>
                <Select.Option value="مراسلات">مراسلات</Select.Option>
                <Select.Option value="مستند عام">مستند عام</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item label="ملاحظات">
              <Input.TextArea
                value={documentDetails.notes}
                onChange={(e) =>
                  setDocumentDetails({
                    ...documentDetails,
                    notes: e.target.value,
                  })
                }
                placeholder="أضف أي ملاحظات إضافية حول المستند (اختياري)"
                rows={3}
                className="text-right"
              />
            </Form.Item>
          </Form>
        </div>
      </Modal>

      <AddOpponentModal
        isOpen={showOpponentModal}
        onClose={() => setShowOpponentModal(false)}
        onOpponentAdded={(opponent, autoNextStep) =>
          handleOpponentSuccess(opponent, autoNextStep)
        }
        showSuccessMessage={!editingOpponent}
        autoNextStep={true}
      />

      <AddPaymentModal
        visible={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={handlePaymentSuccess}
        clientData={{
          id: clientData.id,
          name: clientData.name,
          phone: clientData.phone,
          email: clientData.email,
          address: clientData.address,
        }}
        caseData={{
          id: caseData.id,
          title: caseData.title,
          case_number: caseData.case_number,
          agreed_fees: caseData.agreed_fees,
          client_id: clientData.id,
          status: "pending",
        }}
      />

      {/* أزرار التنقل المخصصة */}
      <div
        className="flex justify-between mt-6 pt-4 border-t border-gray-200"
        dir="rtl"
      >
        <div>
          {currentStep > 1 && (
            <Button onClick={prevStep} icon={<ArrowRightOutlined />}>
              السابق
            </Button>
          )}
        </div>
        <div>
          <Button onClick={onClose} className="ml-2">
            إلغاء
          </Button>
          <Button
            type="primary"
            onClick={
              currentStep === steps.length ? completeRegistration : nextStep
            }
            loading={loading}
            icon={
              currentStep === steps.length ? (
                <CheckCircleOutlined />
              ) : (
                <ArrowLeftOutlined />
              )
            }
          >
            {currentStep === steps.length ? "إكمال التسجيل" : "التالي"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RegistrationWizard;
